import { cn } from "@/zapi-frontend/shared/lib/utils";
import type { TLedgerFigure } from "@/zapi-frontend/shared/types/modelLedger";
import { formatValue } from "./ledgerFormat";
import type { TFormulaPart } from "./ledgerFormat";

// A pill never grows past its line: a long name is cut with an ellipsis rather than the pill
// running under the edge of the panel.
const PILL =
  "typo-caption inline-flex h-7 min-w-0 max-w-full items-center gap-1.5 rounded-full bg-background px-2.5 text-foreground ring-1 ring-foreground/10 transition-colors";
const SYMBOL = "typo-label-md inline-flex h-7 items-center px-0.5 text-muted-foreground";
const OPERATOR_GLYPHS: Record<string, string> = { x: "×", "/": "÷", "-": "−" };

// An operand of a formula: the figure's name with its value. Clicking it opens the figure it names
// and scrolls to it, so a derivation can be followed down to the stated numbers.
function VariablePill({
  figure,
  fallbackKey,
  onReveal
}: {
  figure: TLedgerFigure | undefined;
  fallbackKey: string;
  onReveal: (key: string) => void;
}) {
  const content = (
    <>
      <span className="min-w-0 truncate">{figure?.name ?? fallbackKey}</span>
      {figure && (
        <span className="shrink-0 whitespace-nowrap tabular-nums text-muted-foreground">
          {formatValue(figure.value, figure.unit, figure.dp)}
        </span>
      )}
    </>
  );
  if (!figure) return <span className={cn(PILL, "text-destructive")}>{content}</span>;
  return (
    <button
      type="button"
      data-testid={`ledger-ref-${figure.key}`}
      onClick={(event) => {
        event.stopPropagation();
        onReveal(figure.key);
      }}
      className={cn(PILL, "outline-none hover:ring-foreground/25 focus-visible:ring-3 focus-visible:ring-ring/50")}
    >
      {content}
    </button>
  );
}

// The design draws an operator together with the operand it precedes, and a base together with
// its power, as one statement; the row wraps between statements, never inside one.
const statements = (parts: TFormulaPart[]): TFormulaPart[][] => {
  const groups: TFormulaPart[][] = [];
  for (const part of parts) {
    const last = groups[groups.length - 1];
    const tail = last?.[last.length - 1];
    const operand = part.kind === "ref" || part.kind === "number" || part.kind === "func";
    if (last && operand && last.length === 1 && tail?.kind === "op") last.push(part);
    else if (last && part.kind === "power" && tail && tail.kind !== "op" && tail.kind !== "paren") last.push(part);
    else groups.push([part]);
  }
  return groups;
};

function FormulaPart({
  part,
  byKey,
  onReveal
}: {
  part: TFormulaPart;
  byKey: Map<string, TLedgerFigure>;
  onReveal: (key: string) => void;
}) {
  if (part.kind === "ref") {
    return <VariablePill figure={byKey.get(part.key)} fallbackKey={part.key} onReveal={onReveal} />;
  }
  if (part.kind === "func") return <span className={cn(PILL, "font-medium")}>{part.name}</span>;
  if (part.kind === "number") return <span className={cn(SYMBOL, "tabular-nums")}>{part.text}</span>;
  if (part.kind === "power") {
    const [only] = part.parts;
    return (
      <span className="inline-flex h-6 items-center gap-0.5 text-[12px] leading-[18px] text-innoveraZinc-500">
        {part.parts.length === 1 && only.kind === "number" ? (
          `^${only.text}`
        ) : (
          <>
            ^<FormulaParts parts={part.parts} byKey={byKey} onReveal={onReveal} />
          </>
        )}
      </span>
    );
  }
  return <span className={SYMBOL}>{OPERATOR_GLYPHS[part.text] ?? part.text}</span>;
}

export function FormulaParts({
  parts,
  byKey,
  onReveal
}: {
  parts: TFormulaPart[];
  byKey: Map<string, TLedgerFigure>;
  onReveal: (key: string) => void;
}) {
  return (
    <>
      {statements(parts).map((group, i) =>
        group.length === 1 ? (
          <FormulaPart key={i} part={group[0]} byKey={byKey} onReveal={onReveal} />
        ) : (
          <span key={i} className="inline-flex max-w-full items-center gap-1">
            {group.map((part, j) => (
              <FormulaPart key={j} part={part} byKey={byKey} onReveal={onReveal} />
            ))}
          </span>
        )
      )}
    </>
  );
}
